/**
 * LinkifiedText — renders a plain-text email body with its URLs, bare hostnames
 * and email addresses turned into real links.
 *
 * HTML bodies go through {@link EmailHtmlBody} (sanitized + sandboxed iframe),
 * which keeps the sender's own anchors. A TEXT-only body had no links at all —
 * it was dropped into a `whitespace-pre-wrap` div, so a URL rendered as inert
 * characters the reader had to copy by hand.
 *
 * The text stays UNTRUSTED, so this never builds markup from it: the string is
 * tokenized and emitted as React nodes (`<a>` for the matches, plain strings for
 * everything else). There is no `dangerouslySetInnerHTML`, so markup inside the
 * body can never become markup on the page, and only `http(s):` / `mailto:`
 * hrefs are ever produced — a `javascript:` or `data:` run in the text is not a
 * match and stays plain text.
 *
 * Links open detached (`target="_blank"`, `rel="noopener noreferrer nofollow"`),
 * matching the hardening the HTML path applies to the sender's own anchors.
 */

import { Fragment, type ReactNode } from 'react';

// Candidate tokens, in precedence order: an absolute http(s) URL, a bare
// `www.`-prefixed host (very common in text bodies), or an email address.
// The URL tails are deliberately greedy — a URL's legal character set overlaps
// sentence punctuation, so the real boundary is decided by `trimTail` below
// rather than by the pattern.
const LINK_RE =
  /(https?:\/\/[^\s<>"'`]+|www\.[^\s<>"'`]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)/g;

/** Sentence punctuation that ends the prose, not the URL. */
const TRAILING_PUNCT = /[.,;:!?'"]+$/;

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;

/** Closing brackets, and the opener each one pairs with. */
const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

const countOf = (s: string, ch: string) => s.split(ch).length - 1;

/**
 * Splits a matched token into the part that is really the link and the trailing
 * prose punctuation. A closing bracket is kept only when the token also opens
 * one — so `…/wiki/Foo_(bar)` keeps its `)` while `(see https://x.com)` does not.
 */
export function trimLinkTail(token: string): { link: string; tail: string } {
  let link = token;
  for (;;) {
    const before = link;
    link = link.replace(TRAILING_PUNCT, '');
    const last = link.slice(-1);
    const opener = CLOSERS[last];
    if (opener && countOf(link, last) > countOf(link, opener)) link = link.slice(0, -1);
    if (link === before) break;
  }
  return { link, tail: token.slice(link.length) };
}

/** The href for a trimmed token, or null when it isn't linkable. */
function hrefFor(token: string): string | null {
  if (!token) return null;
  if (EMAIL_RE.test(token)) return `mailto:${token}`;
  if (/^https?:\/\//i.test(token)) return token;
  if (/^www\./i.test(token)) return `https://${token}`;
  return null;
}

/** Splits `text` into interleaved plain-string and `<a>` nodes. */
export function linkifyToNodes(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  LINK_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = LINK_RE.exec(text)) !== null) {
    const { link, tail } = trimLinkTail(m[0]);
    const href = hrefFor(link);
    if (!href) continue;
    if (m.index > cursor) out.push(text.slice(cursor, m.index));
    out.push(
      <a
        key={`l${key++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="text-primary underline underline-offset-2 hover:opacity-80"
      >
        {link}
      </a>,
    );
    if (tail) out.push(tail);
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

export interface LinkifiedTextProps {
  text: string;
  className?: string;
}

export function LinkifiedText({ text, className }: LinkifiedTextProps) {
  return (
    <div className={className}>
      {linkifyToNodes(text).map((node, i) => (
        <Fragment key={i}>{node}</Fragment>
      ))}
    </div>
  );
}
